import { faGear } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { Icon } from '@/components/ui/icon';
import { MutedText } from '@/components/ui/typography';
import type { CollectionCardField } from '@/lib/blocks/collection/blocks';
import {
  injectPlaceholdersForField,
  resolveSymmetricCollectionProps,
} from '@/lib/blocks/collection/blocks';
import { getCollectionVariants } from '@/lib/blocks/collection/variant-registry';
import { type GroupChildFloat, getBlockFloat } from '@/lib/blocks/layout/group-layout';
import { scheduleScrollToPageEditorBlock } from '@/lib/page-editor/scroll-to-block';
import { cn } from '@/lib/utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import { getBlockUiKey, type PageBlock } from '@/types/block';
import { BlockActionButton } from '../toolbar/block-action-button';
import { BackgroundTab } from './settings/background-tab';
import { ContentTab } from './settings/content-tab';
import { LayoutTab } from './settings/layout-tab';
import { SettingsPopoverShell } from './settings/settings-popover-shell';
import { SettingsTab } from './settings/settings-tab';
import { useCollectionSettings } from './settings/use-collection-settings';

interface CollectionSettingsDialogProps {
  sectionId: string;
  block: PageBlock;
  isVisible: boolean;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  cardTemplateBlock?: PageBlock;
  cardTemplateChildren?: PageBlock[];
  showFloatOption?: boolean;
}

export function CollectionSettingsDialog({
  sectionId,
  block,
  isVisible,
  disabled,
  onOpenChange,
  cardTemplateBlock,
  cardTemplateChildren = [],
  showFloatOption = false,
}: CollectionSettingsDialogProps) {
  const batchUpdateBlocks = usePageEditorStore((state) => state.batchUpdateBlocks);
  const variants = getCollectionVariants();

  const settings = useCollectionSettings({
    sectionId,
    block,
    cardTemplateBlock,
    cardTemplateChildren,
    variants,
    onOpenChange,
  });

  const handleFieldsChange = (nextFields: CollectionCardField[]) => {
    settings.setDraftFields(nextFields);

    const currentItems = resolveSymmetricCollectionProps(block).items;
    let updatedItems = currentItems;

    const batchedUpdates: Array<{ blockId: string; updates: Partial<PageBlock> }> = [];

    nextFields.forEach((field, index) => {
      if (field.blockId) {
        batchedUpdates.push({
          blockId: field.blockId,
          updates: { visible: field.enabled, position: index + 1 },
        });
      }
      if (field.enabled) {
        updatedItems = injectPlaceholdersForField(updatedItems, field);
      }
    });

    if (updatedItems !== currentItems) {
      batchedUpdates.push({
        blockId: block.id,
        updates: { props: { ...(block.props ?? {}), items: updatedItems } },
      });
    }

    if (batchedUpdates.length > 0) {
      batchUpdateBlocks(sectionId, batchedUpdates);
    }
  };

  const handleFloatChange = (float: GroupChildFloat | null) => {
    if (float === getBlockFloat(block)) return;
    settings.patchBlockProps({ float });
    scheduleScrollToPageEditorBlock(getBlockUiKey(block));
  };

  if (variants.length === 0) return null;

  const isAccordionVariant = settings.activeVariantEntry?.renderer === 'accordion';

  return (
    <SettingsPopoverShell
      open={settings.open}
      onOpenChange={settings.handleOpenChange}
      title="Collection Settings"
      trackMovingAnchor={showFloatOption}
      trigger={
        <BlockActionButton
          icon={<Icon icon={faGear} />}
          label="Collection Settings"
          srLabel="Edit collection settings"
          showLabel
          isVisible={isVisible}
          disabled={disabled}
          className={cn(settings.open && 'bg-primary/7')}
          onPointerDown={(e) => e.stopPropagation()}
        />
      }
      layoutContent={
        <LayoutTab variant={settings.draftVariant} onVariantChange={settings.handleVariantChange} />
      }
      contentContent={
        <ContentTab
          fields={settings.draftFields}
          onFieldsChange={handleFieldsChange}
          disabledImageField={isAccordionVariant}
          staticImageField={settings.activeVariantEntry?.renderer === 'alternating'}
        />
      }
      backgroundContent={
        <BackgroundTab
          variantProps={settings.activeVariantProps}
          onVariantPropsChange={settings.handleVariantPropsChange}
        />
      }
      settingsContent={
        settings.activeVariantEntry ? (
          <SettingsTab
            variantProps={settings.activeVariantProps}
            renderer={settings.activeVariantEntry.renderer}
            cardAlign={settings.draftCardAlign}
            onVariantPropsChange={settings.handleVariantPropsChange}
            onCardAlignChange={settings.handleCardAlignChange}
            showImagePosition={settings.draftFields.some(
              (field: CollectionCardField) => field.kind === 'image',
            )}
            imageAspect={settings.imageAspect}
            onImageAspectChange={settings.handleImageAspectChange}
            float={getBlockFloat(block)}
            onFloatChange={showFloatOption ? handleFloatChange : undefined}
          />
        ) : (
          <MutedText>No settings available.</MutedText>
        )
      }
    />
  );
}
